import type { Fields, RootConfig } from '@puckeditor/core';

import { SURFACE_COLOR_FIELD_AI_SCHEMA, SurfaceColorField } from '../fields/widgets/surface-color-field';
import { toggleField } from '../fields/widgets/toggle-field';
import { PageRootRender } from './page-root-render';

/** Page-level appearance and chrome fields shared by every CMS editor's root config. */
const SHARED_ROOT_FIELDS: Fields<{ backgroundColor: string; showHeader: boolean; showNavigation: boolean }> = {
  backgroundColor: {
    type: 'custom',
    label: 'Page background',
    render: SurfaceColorField,
    ai: {
      schema: SURFACE_COLOR_FIELD_AI_SCHEMA,
      instructions:
        "Set to a surface role ('none', 'surface', 'muted') so it follows merchant branding; use a hex value only when a specific brand color is required.",
    },
  },
  showHeader: toggleField('Header', undefined, undefined, { defaultOn: true }),
  showNavigation: toggleField('Navigation', undefined, undefined, { defaultOn: true }),
};

const SHARED_ROOT_DEFAULT_PROPS = {
  backgroundColor: 'surface',
  showHeader: true,
  showNavigation: true,
};

export type CreatePageRootConfigOptions = {
  /** Page-model-specific fields (e.g. standalone-page's `urlPath`), merged alongside the shared Header/Navigation fields. */
  fields?: Fields;
  /** Page-model-specific default prop values, merged alongside the shared Header/Navigation defaults. */
  defaultProps?: Record<string, unknown>;
  /** AI authoring instructions for this page model's root. Omit entirely for models that don't need AI support. */
  ai?: CmsComponentAiConfig;
};

/**
 * Builds the root Puck config shared by both CMS editors (standalone page + menu landing
 * page): the background and Header/Navigation fields and defaults, and the shared
 * PageRootRender function. Page-specific fields/defaultProps (e.g. standalone-page's
 * `urlPath`) are merged in ahead of the shared ones so they render first in the sidebar.
 */
export function createPageRootConfig({ fields, defaultProps, ai }: CreatePageRootConfigOptions = {}): RootConfig {
  return {
    fields: { ...fields, ...SHARED_ROOT_FIELDS },
    defaultProps: { ...defaultProps, ...SHARED_ROOT_DEFAULT_PROPS },
    ...(ai ? { ai } : {}),
    render: PageRootRender,
  };
}
